import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import mermaid from 'mermaid';
import StrategyAgent from './StrategyAgent';

// Serialize mermaid.render() calls
let mermaidRenderQueue = Promise.resolve();
function queuedMermaidRender(id, text) {
  return new Promise((resolve, reject) => {
    mermaidRenderQueue = mermaidRenderQueue.then(async () => {
      try {
        const result = await mermaid.render(id, text);
        resolve(result);
      } catch (err) {
        reject(err);
      }
    });
  });
}

function sanitizeMermaid(text) {
  let s = text;
  s = s.replace(/\|>/g, '|');
  s = s.replace(/(-->|---)\s*\{([^}]+)\}\s*([A-Za-z_]\w*)/g, '$1 $3{$2}');
  let decCounter = 0;
  s = s.replace(/^(\s*)\{([^}]+)\}\s*(-->|---)/gm, (match, indent, label, arrow) => {
    decCounter++;
    return `${indent}dec${decCounter}{${label}} ${arrow}`;
  });
  s = s.replace(/\|([^|]*)\|/g, (match, label) => '|' + label.replace(/[";{}()[\]]/g, '') + '|');
  s = s.replace(/\[([^\]]*)\]/g, (match, label) => '[' + label.replace(/[";{}()]/g, '') + ']');
  s = s.replace(/\{([^}]*)\?([^}]*)\}/g, '{$1$2}');
  // Fix gantt date formats that might have issues
  s = s.replace(/dateFormat\s+.*/g, (match) => match.trim());
  return s;
}

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => { const h = () => setM(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return m;
}

// Format numbers with thousands separators (e.g., $2100 → $2,100)
function fmt(str) {
  if (!str || typeof str !== 'string') return str;
  return str.replace(/\$(\d{1,3})(\d{3})\b/g, (_, a, b) => `$${a},${b}`)
            .replace(/\$(\d{1,3})(\d{3})(\d{3})\b/g, (_, a, b, c) => `$${a},${b},${c}`);
}

function StrategyDiagramRenderer({ diagramData }) {
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);

  const diagram = diagramData?.mermaidDiagram || diagramData;
  const title = diagramData?.title || 'Diagram';

  useEffect(() => {
    if (!diagram) return;
    const renderDiagram = async () => {
      let diagramText = typeof diagram === 'string' ? diagram.replace(/\\n/g, '\n') : '';
      diagramText = sanitizeMermaid(diagramText);
      try {
        const id = 'strategy-diagram-' + Math.random().toString(36).substr(2, 9);
        const { svg } = await queuedMermaidRender(id, diagramText);
        setSvgContent(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err, '\nDiagram text:', diagramText);
        setError('Could not render diagram');
      }
    };
    renderDiagram();
  }, [diagram]);

  // Close fullscreen on Escape
  useEffect(() => {
    if (!fullscreen) return;
    const handler = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [fullscreen]);

  if (!diagram) return null;

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
          {svgContent && (
            <button onClick={() => setFullscreen(true)}
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-600 flex items-center gap-1">
              ⛶ Fullscreen
            </button>
          )}
        </div>
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
        ) : svgContent ? (
          <div ref={containerRef} className="overflow-x-auto bg-white border border-gray-200 rounded-lg p-4 flex justify-center cursor-pointer"
            onClick={() => setFullscreen(true)}
            dangerouslySetInnerHTML={{ __html: svgContent }} />
        ) : (
          <div className="flex items-center justify-center p-8 text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mr-3"></div>
            Rendering diagram...
          </div>
        )}
      </div>

      {/* Fullscreen overlay — rendered via portal to escape overflow:hidden containers */}
      {fullscreen && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.9)', display: 'flex', flexDirection: 'column' }}
          onClick={() => setFullscreen(false)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', flexShrink: 0 }}>
            <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, margin: 0 }}>{title}</h3>
            <button onClick={() => setFullscreen(false)}
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              ✕ Close (Esc)
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: 'white', borderRadius: 12, padding: 32, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', minWidth: '60vw' }}
              dangerouslySetInnerHTML={{ __html: svgContent }} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function AskStrategyQuestion({ sectionKey, contextText, sectionType, strategyUuid, questions, onNewQuestion }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  const sectionQs = (questions || []).filter(q => q.section_key === sectionKey);

  useEffect(() => {
    if (open && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [sectionQs.length, open]);

  const handleAsk = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    try {
      const response = await fetch(`/api/legal-strategy/${strategyUuid}/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: input.trim(), sectionKey, contextText, sectionType })
      });
      const data = await response.json();
      if (data.success) {
        onNewQuestion(data.question);
        setInput('');
      }
    } catch (err) {
      console.error('Question error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 text-gray-400 hover:text-indigo-600 hover:border-indigo-400 transition-colors text-xs font-bold flex-shrink-0"
        title={sectionQs.length > 0 ? `${sectionQs.length} question(s) asked` : 'Ask a question about this section'}
      >
        {sectionQs.length > 0 ? `${sectionQs.length}` : '?'}
      </button>

      {open && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: '520px', zIndex: 1000, display: 'flex', flexDirection: 'column', background: 'white', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)' }}>
          <div style={{ padding: '16px 20px', background: '#312e81', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>Ask about: {sectionType || sectionKey}</h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', opacity: 0.8 }}>Ask questions or suggest modifications</p>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '13px' }}>Close</button>
          </div>

          {contextText && (
            <div style={{ padding: '10px 20px', background: '#f5f3ff', fontSize: '12px', color: '#5b21b6', borderBottom: '1px solid #ede9fe', maxHeight: '80px', overflow: 'hidden' }}>
              {contextText.substring(0, 200)}{contextText.length > 200 ? '...' : ''}
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            {sectionQs.length === 0 && !loading && (
              <p style={{ textAlign: 'center', color: '#999', fontSize: '13px', marginTop: '40px' }}>No questions yet. Ask anything about this section or suggest changes to the strategy.</p>
            )}
            {sectionQs.map((q, i) => (
              <div key={q.id || i} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                  <div style={{ maxWidth: '85%', padding: '10px 14px', background: '#4f46e5', color: 'white', borderRadius: '14px 14px 2px 14px', fontSize: '13px' }}>{q.question}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ maxWidth: '85%', padding: '10px 14px', background: '#f3f4f6', borderRadius: '14px 14px 14px 2px', fontSize: '13px', color: '#333', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{q.answer}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '8px' }}>
                <div style={{ padding: '10px 14px', background: '#f3f4f6', borderRadius: '14px', fontSize: '13px', color: '#999' }}>Thinking...</div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAsk(); if (e.key === 'Escape') setOpen(false); }}
              placeholder="Ask a question or suggest a modification..."
              autoFocus
              style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
            />
            <button
              onClick={handleAsk}
              disabled={!input.trim() || loading}
              style={{ padding: '10px 18px', background: (!input.trim() || loading) ? '#d1d5db' : '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', cursor: (!input.trim() || loading) ? 'default' : 'pointer', fontSize: '13px', fontWeight: '600' }}
            >Ask</button>
          </div>
        </div>
      )}
    </>
  );
}

function ExplainPlayer({ projectId, piktoApi }) {
  const [scenes, setScenes] = useState([]);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!projectId || !piktoApi) return;
    (async () => {
      try {
        const r = await fetch(`${piktoApi}/api/projects/${projectId}?includeScenes=true`);
        const d = await r.json();
        const s = d.project?.scenes || [];
        setScenes(s);
        // Don't auto-play — browsers block autoplay audio without user gesture
      } catch {}
      setLoading(false);
    })();
  }, [projectId, piktoApi]);

  // Auto-advance scenes
  useEffect(() => {
    if (!playing || scenes.length === 0) return;
    const scene = scenes[current];
    const duration = scene?.audio?.duration ? scene.audio.duration : (scene?.duration || 5) * 1000;

    // Play audio — prepend pikto URL if relative
    if (audioRef.current && scene?.audio?.url) {
      let audioUrl = scene.audio.url;
      if (audioUrl.startsWith('/')) audioUrl = (piktoApi || 'https://pikto.us') + audioUrl;
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(() => {});
    }

    timerRef.current = setTimeout(() => {
      if (current < scenes.length - 1) {
        setCurrent(c => c + 1);
      } else {
        // End — stop playing, don't loop
        setPlaying(false);
      }
    }, duration);

    return () => {
      clearTimeout(timerRef.current);
      // Pause audio on cleanup (when pausing or unmounting)
      if (audioRef.current) { audioRef.current.pause(); }
    };
  }, [current, playing, scenes]);

  if (loading) {
    return (
      <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading animation...</div>
    );
  }

  if (scenes.length === 0) {
    return <div style={{ color: '#9ca3af', fontSize: 14 }}>No scenes available</div>;
  }

  const scene = scenes[current];
  const icons = scene?.icons || [];
  const message = scene?.transcript || scene?.message || '';

  return (
    <div style={{ width: '100%', maxWidth: 900, position: 'relative' }}>
      <audio ref={audioRef} />
      {/* Scene display */}
      <div style={{
        background: 'white', borderRadius: 12, overflow: 'hidden',
        aspectRatio: window.innerWidth < 768 ? '9/16' : '16/9',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '40px 32px', position: 'relative',
      }}>
        {/* Script text at top */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, padding: '20px 32px',
          background: 'rgba(255,255,255,0.95)', borderBottom: '1px solid #e5e7eb',
        }}>
          <p style={{ fontSize: 18, fontWeight: 500, color: '#1f2937', lineHeight: 1.5, textAlign: 'center', margin: 0 }}>
            {message}
          </p>
        </div>

        {/* Icons in center */}
        <div style={{ display: 'flex', gap: 48, alignItems: 'center', justifyContent: 'center', marginTop: 60 }}>
          {icons.map((ic, i) => {
            const iconData = ic.iconId || {};
            const url = iconData.cloudFrontUrl || iconData.s3Url;
            return (
              <div key={i} style={{ textAlign: 'center' }}>
                {url ? (
                  <img src={url} alt={ic.label || ''} style={{ width: 120, height: 120, objectFit: 'contain' }}
                    crossOrigin="anonymous" />
                ) : (
                  <div style={{ width: 120, height: 120, background: '#f3f4f6', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>
                    ?
                  </div>
                )}
                {ic.label && (
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginTop: 8 }}>{ic.label}</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Watermark */}
        <div style={{ position: 'absolute', bottom: 12, right: 16, fontSize: 14, color: '#d1d5db', fontWeight: 600 }}>
          pikto.us
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
        {scenes.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i === current ? '#818cf8' : i < current ? '#c7d2fe' : 'rgba(255,255,255,0.15)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      {/* Play/pause controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 12 }}>
        <button
          onClick={() => setCurrent(c => Math.max(0, c - 1))}
          style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#6b7280' }}
        >Prev</button>
        <button
          onClick={() => setPlaying(p => !p)}
          style={{ background: playing ? '#f3f4f6' : '#1a1a1a', color: playing ? '#1a1a1a' : '#fff', border: 'none', borderRadius: 8, padding: '8px 24px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
        >{playing ? 'Pause' : 'Play'}</button>
        <button
          onClick={() => setCurrent(c => Math.min(scenes.length - 1, c + 1))}
          style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#6b7280' }}
        >Next</button>
        <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>{current + 1} / {scenes.length}</span>
      </div>
    </div>
  );
}

function ExplainProgress({ progress = 0, stage = 'queued', message }) {
  const stages = ['storyline', 'scenes', 'icons', 'audio', 'animations', 'complete'];
  const labels = { queued: 'Queued', storyline: 'Storyline', scenes: 'Scenes', icons: 'Icons', audio: 'Audio', animations: 'Animations', complete: 'Done' };
  const currentIdx = stages.indexOf(stage);
  return (
    <div style={{ textAlign: 'center', color: 'white', maxWidth: 440, padding: '0 20px' }}>
      <p style={{ fontSize: 18, fontWeight: 600, color: '#e0e7ff', marginBottom: 6 }}>{message || 'Creating explanation animation...'}</p>
      <p style={{ fontSize: 32, fontWeight: 700, color: '#a5b4fc', marginBottom: 16 }}>{Math.round(progress)}%</p>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ height: '100%', background: '#818cf8', borderRadius: 3, width: `${Math.max(progress, 2)}%`, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
        {stages.map((s, idx) => {
          const isDone = idx < currentIdx;
          const isCurrent = s === stage;
          return (
            <div key={s} style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: isDone ? 'rgba(134,239,172,0.2)' : isCurrent ? 'rgba(129,140,248,0.3)' : 'rgba(255,255,255,0.06)',
              color: isDone ? '#86efac' : isCurrent ? '#c7d2fe' : '#6b7280',
              border: isCurrent ? '1px solid rgba(129,140,248,0.5)' : '1px solid transparent',
            }}>
              {isDone ? '\u2713 ' : ''}{labels[s] || s}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LegalStrategy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Form state
  const [goalText, setGoalText] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [riskTolerance, setRiskTolerance] = useState('moderate');
  const [budgetConstraints, setBudgetConstraints] = useState('');
  const [timeline, setTimeline] = useState('');

  // Strategy state
  const [strategy, setStrategy] = useState(null);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTabRaw] = useState(searchParams.get('section') || 'overview');
  const setActiveTab = (tab) => { setActiveTabRaw(tab); setSearchParams(prev => { prev.set('section', tab); return prev; }, { replace: true }); };
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [simulations, setSimulations] = useState(null);
  const [simulatingRisks, setSimulatingRisks] = useState(false);
  const [faqs, setFaqs] = useState({});
  const [pathwayActions, setPathwayActions] = useState({});
  const [generatingActions, setGeneratingActions] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [explainVideo, setExplainVideo] = useState(null);
  const [cachedExplainJob, setCachedExplainJob] = useState(null);
  const [linkedServices, setLinkedServices] = useState({ llc: null, tax: null });

  // Check for linked LLC formations and tax strategies
  useEffect(() => {
    if (strategy?.uuid && strategy?.status === 'completed') {
      // Check LLC formations linked to this strategy
      fetch('/api/legal-services?type=llc', { credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          if (d.success && d.requests) {
            const linked = d.requests.find(r => r.form_data?.strategyId === strategy.uuid);
            if (linked) setLinkedServices(prev => ({ ...prev, llc: linked }));
          }
        }).catch(() => {});
      // Check tax strategies linked to this strategy
      fetch('/api/tax-strategies', { credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          if (d.success && d.strategies) {
            const linked = d.strategies.find(s => s.legal_strategy_id === strategy.uuid || s.form_data?.legalStrategyId === strategy.uuid);
            if (linked) setLinkedServices(prev => ({ ...prev, tax: linked }));
          }
        }).catch(() => {});
    }
  }, [strategy?.uuid, strategy?.status]);

  // Load existing explain job on strategy load — store in cache but DON'T open modal
  useEffect(() => {
    if (strategy?.uuid && strategy?.status === 'completed') {
      fetch(`/api/legal-strategy/${strategy.uuid}/explain`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => { if (d.success && d.status !== 'none') setCachedExplainJob(d); })
        .catch(() => {});
    }
  }, [strategy?.uuid, strategy?.status]);

  // Poll for explain progress when processing
  useEffect(() => {
    if (!explainVideo || explainVideo.status !== 'processing' || !strategy?.uuid) return;
    const poll = setInterval(async () => {
      try {
        const r = await fetch(`/api/legal-strategy/${strategy.uuid}/explain`, { credentials: 'include' });
        const d = await r.json();
        if (d.success) {
          setExplainVideo(d);
          setCachedExplainJob(d);
          if (d.status !== 'processing') clearInterval(poll);
        }
      } catch {}
    }, 3000);
    return () => clearInterval(poll);
  }, [explainVideo?.status, strategy?.uuid]);
  const [userData, setUserData] = useState({});
  const chatEndRef = useRef(null);
  const [questions, setQuestions] = useState([]);

  // Sidebar state
  const [strategies, setStrategies] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');

  const pollRef = useRef(null);

  // Load strategies list
  const loadStrategies = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      const response = await fetch(`/api/legal-strategies?${params}`);
      const data = await response.json();
      if (data.success) setStrategies(data.strategies || []);
    } catch (err) {
      console.error('Error loading strategies:', err);
    }
  }, [searchQuery]);

  const loadFolders = useCallback(async () => {
    try {
      const response = await fetch('/api/strategy-folders');
      const data = await response.json();
      if (data.success) setFolders(data.folders || []);
    } catch (err) {
      console.error('Error loading folders:', err);
    }
  }, []);

  // Load questions for a strategy
  const loadQuestions = useCallback(async (uuid) => {
    try {
      const response = await fetch(`/api/legal-strategy/${uuid}/questions`);
      const data = await response.json();
      if (data.success) setQuestions(data.questions || []);
    } catch (err) {
      console.error('Error loading questions:', err);
    }
  }, []);

  // Load strategy by UUID
  const loadStrategy = useCallback(async (uuid) => {
    try {
      const response = await fetch(`/api/legal-strategy/${uuid}`);
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch (parseErr) {
        console.error('JSON parse error loading strategy:', parseErr.message);
        setError('Failed to load strategy data. The response may be corrupted.');
        return null;
      }
      if (data.success) {
        setStrategy(data.strategy);
        setPhases(data.phases || []);
        if (data.strategy.status === 'failed') {
          setError(data.strategy.error_message || 'Strategy generation failed.');
        } else {
          loadQuestions(uuid);
        }
        return data.strategy;
      }
    } catch (err) {
      console.error('Error loading strategy:', err);
      setError('Failed to load strategy.');
    }
    return null;
  }, [loadQuestions]);

  // Polling
  const startPolling = useCallback((uuid) => {
    if (pollRef.current) clearInterval(pollRef.current);

    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/legal-strategy/${uuid}`);
        const data = await response.json();
        if (data.success && data.strategy) {
          setStrategy(data.strategy);
          setPhases(data.phases || []);
          if (data.strategy.status === 'completed') {
            setLoading(false);
            clearInterval(interval);
            pollRef.current = null;
            loadStrategies();
          } else if (data.strategy.status === 'failed') {
            setError(data.strategy.error_message || 'Strategy generation failed.');
            setLoading(false);
            clearInterval(interval);
            pollRef.current = null;
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    pollRef.current = interval;

    // 10-minute timeout
    setTimeout(() => {
      if (pollRef.current === interval) {
        clearInterval(interval);
        pollRef.current = null;
        setError('Strategy generation timed out. Please try again.');
        setLoading(false);
      }
    }, 600000);
  }, [loadStrategies]);

  useEffect(() => {
    loadStrategies();
    loadFolders();
  }, [loadStrategies, loadFolders]);

  useEffect(() => {
    if (id) {
      loadStrategy(id).then(s => {
        if (s && (s.status === 'processing' || s.status === 'pending')) {
          setLoading(true);
          startPolling(id);
        }
      });
    } else {
      setStrategy(null);
      setPhases([]);
      setError(null);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id, loadStrategy, startPolling]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!goalText.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/legal-strategy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goal_text: goalText.trim(),
          jurisdiction: jurisdiction || undefined,
          budget_constraints: budgetConstraints || undefined,
          timeline: timeline || undefined,
          risk_tolerance: riskTolerance
        })
      });
      const data = await response.json();
      if (data.success) {
        setGoalText('');
        setJurisdiction('');
        setBudgetConstraints('');
        setTimeline('');
        setRiskTolerance('moderate');
        navigate(`/legal-strategy/${data.uuid}`);
        setLoading(true);
        startPolling(data.uuid);
      } else {
        setError(data.error || 'Failed to create strategy');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewQuestion = (q) => {
    setQuestions(prev => [...prev, q]);
  };

  const handleRerun = async () => {
    if (!strategy) return;
    try {
      const response = await fetch(`/api/legal-strategy/${strategy.uuid}/rerun`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setLoading(true);
        setError(null);
        setPhases([]);
        startPolling(strategy.uuid);
      }
    } catch (err) {
      setError('Failed to re-run strategy.');
    }
  };

  const handleDelete = async (uuid) => {
    if (!window.confirm('Delete this strategy?')) return;
    try {
      await fetch(`/api/legal-strategy/${uuid}`, { method: 'DELETE' });
      if (strategy?.uuid === uuid) {
        navigate('/legal-strategy');
        setStrategy(null);
        setPhases([]);
      }
      loadStrategies();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleMoveToFolder = async (strategyUuid, folderUuid) => {
    try {
      await fetch(`/api/legal-strategy/${strategyUuid}/folder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderUuid: folderUuid || null })
      });
      loadStrategies();
      loadFolders();
    } catch (err) {
      console.error('Move error:', err);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await fetch('/api/strategy-folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFolderName.trim() })
      });
      setNewFolderName('');
      setShowNewFolder(false);
      loadFolders();
    } catch (err) {
      console.error('Create folder error:', err);
    }
  };

  const handleRenameFolder = async (uuid) => {
    if (!renameFolderName.trim()) return;
    try {
      await fetch(`/api/strategy-folders/${uuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: renameFolderName.trim() })
      });
      setRenamingFolder(null);
      setRenameFolderName('');
      loadFolders();
    } catch (err) {
      console.error('Rename folder error:', err);
    }
  };

  const handleDeleteFolder = async (uuid) => {
    if (!window.confirm('Delete this folder? Strategies will be moved to unfiled.')) return;
    try {
      await fetch(`/api/strategy-folders/${uuid}`, { method: 'DELETE' });
      if (selectedFolder === uuid) setSelectedFolder('all');
      loadFolders();
      loadStrategies();
    } catch (err) {
      console.error('Delete folder error:', err);
    }
  };

  // Get phase data helper
  const getPhaseData = (phaseName) => {
    const phase = phases.find(p => p.phase_name === phaseName);
    if (!phase) return null;
    return typeof phase.phase_data === 'string' ? JSON.parse(phase.phase_data) : phase.phase_data;
  };

  // Filter strategies for sidebar
  const filteredStrategies = strategies.filter(s => {
    if (selectedFolder === 'unfiled') return !s.folder_id;
    if (selectedFolder !== 'all') {
      const folder = folders.find(f => f.uuid === selectedFolder);
      if (folder) return s.folder_id === folder.id;
    }
    return true;
  });

  const diagrams = strategy?.diagrams ? (typeof strategy.diagrams === 'string' ? JSON.parse(strategy.diagrams) : strategy.diagrams) : null;

  // Load saved actions when switching to a pathway tab
  useEffect(() => {
    if (activeTab.startsWith('pathway-') && strategy?.uuid) {
      const pIdx = parseInt(activeTab.split('-')[1]);
      if (!pathwayActions[pIdx]) {
        (async () => {
          try {
            const r = await fetch(`/api/legal-strategy/${strategy.uuid}/actions/${pIdx}`, { credentials: 'include' });
            const d = await r.json();
            if (d.success && d.actions) {
              setPathwayActions(prev => ({ ...prev, [pIdx]: d.actions }));
              setUserData(prev => ({ ...prev, [pIdx]: d.userData || {} }));
            }
          } catch {}
        })();
      }
    }
  }, [activeTab, strategy?.uuid]);

  // Load FAQs
  useEffect(() => {
    if (strategy?.uuid && strategy?.status === 'completed') {
      (async () => {
        try {
          const r = await fetch(`/api/legal-strategy/${strategy.uuid}/faqs`);
          const d = await r.json();
          if (d.success) setFaqs(d.faqs || {});
        } catch {}
      })();
    }
  }, [strategy?.uuid, strategy?.status]);

  // Load saved simulations from strategy data
  useEffect(() => {
    if (strategy?.simulations && !simulations) {
      const saved = typeof strategy.simulations === 'string' ? JSON.parse(strategy.simulations) : strategy.simulations;
      if (saved?.scenarios) setSimulations(saved);
    }
  }, [strategy?.simulations]);
  const goalAnalysis = getPhaseData('goal_analysis');
  const research = getPhaseData('jurisdictional_research');
  const formulation = getPhaseData('formulation');
  const simulation = getPhaseData('simulation');
  const riskAnalysis = getPhaseData('risk_analysis');
  const synthesis = getPhaseData('synthesis');
  const timelineScenarios = getPhaseData('timeline_scenarios');

  // Derive pathways (backward compatible with old "strategies" format)
  const pathways = formulation?.pathways || formulation?.strategies || [];
  const hasPathwayScores = pathways.some(p => p.scores);
  const simulationResponses = simulation?.strategy_responses || simulation?.responses || [];
  const riskAssessments = riskAnalysis?.risk_assessments || [];

  const statusColor = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-700';
    if (status === 'processing' || status === 'pending') return 'bg-blue-100 text-blue-700';
    if (status === 'failed') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <>
    {/* Explain Video Modal */}
    {explainVideo && ReactDOM.createPortal(
      <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.95)', display: 'flex', flexDirection: 'column' }}
        onClick={() => setExplainVideo(null)}>
        {/* Top bar — minimal, just regenerate + close */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '8px 12px', gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button onClick={async () => {
            setExplaining(true);
            try {
              const r = await fetch(`/api/legal-strategy/${strategy.uuid}/explain`, { method: 'POST', credentials: 'include' });
              const d = await r.json();
              if (d.success) { setExplainVideo(d); setCachedExplainJob(d); }
            } catch {}
            setExplaining(false);
          }} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 14px', color: 'white', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }} title="Regenerate">
            <span>Regenerate</span>
          </button>
          <button onClick={() => setExplainVideo(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 12px', color: 'white', cursor: 'pointer', fontSize: 16, lineHeight: 1 }} title="Close">
            ✕
          </button>
        </div>

        {/* Content area — fills remaining space */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '0 12px 12px' }} onClick={e => e.stopPropagation()}>
          {explainVideo.videoUrl ? (
            <video controls autoPlay muted style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} src={explainVideo.videoUrl} />
          ) : explainVideo.status === 'ready' && explainVideo.projectId ? (
            <ExplainPlayer
              projectId={explainVideo.projectId}
              piktoApi={explainVideo.projectUrl ? explainVideo.projectUrl.replace(/\/project\/.*$/, '') : 'https://pikto.imagia.net'}
            />
          ) : explainVideo.status === 'processing' ? (
            <ExplainProgress progress={explainVideo.progress} stage={explainVideo.stage} message={explainVideo.message} />
          ) : (
            <div style={{ textAlign: 'center', color: 'white', maxWidth: 400 }}>
              <p style={{ fontSize: 16, fontWeight: 500, color: '#e2e8f0', marginBottom: 8 }}>{explainVideo.message || 'Explanation will be available soon.'}</p>
              {explainVideo.status === 'unavailable' && <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 8 }}>The animation service is being restored.</p>}
            </div>
          )}
        </div>
      </div>,
      document.body
    )}

    <style>{`
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>

    {/* Mobile sidebar toggle */}
    {isMobile && (
      <button onClick={() => setShowMobileSidebar(!showMobileSidebar)}
        style={{ position: 'fixed', bottom: 20, left: 20, zIndex: 30, width: 44, height: 44, borderRadius: '50%', border: 'none', background: '#6366f1', color: 'white', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        ☰
      </button>
    )}

    {/* Mobile sidebar overlay */}
    {isMobile && showMobileSidebar && <div onClick={() => setShowMobileSidebar(false)} style={{ position: 'fixed', inset: 0, zIndex: 35, background: 'rgba(0,0,0,0.3)' }} />}

    <div className="px-4 sm:px-6 lg:px-8 py-8" style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: isMobile ? '16px 12px' : undefined }}>
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-80 flex-shrink-0" style={isMobile ? {
          position: 'fixed', left: 0, top: 0, zIndex: 40, width: '85vw', maxWidth: 320, height: '100%',
          background: 'white', boxShadow: '4px 0 12px rgba(0,0,0,0.1)',
          display: showMobileSidebar ? 'block' : 'none', overflowY: 'auto',
        } : {}}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4" style={isMobile ? { border: 'none', borderRadius: 0 } : {}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Your Strategies</h2>
              {isMobile && <button onClick={() => setShowMobileSidebar(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9ca3af' }}>✕</button>}
            </div>

            {/* Folder navigation */}
            <div className="flex flex-wrap gap-1 mb-3">
              <button onClick={() => setSelectedFolder('all')}
                className={`px-2 py-1 text-xs rounded-md ${selectedFolder === 'all' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                All
              </button>
              <button onClick={() => setSelectedFolder('unfiled')}
                className={`px-2 py-1 text-xs rounded-md ${selectedFolder === 'unfiled' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                Unfiled
              </button>
              {folders.map(f => (
                <div key={f.uuid} className="relative group">
                  {renamingFolder === f.uuid ? (
                    <input type="text" value={renameFolderName} onChange={e => setRenameFolderName(e.target.value)}
                      onBlur={() => handleRenameFolder(f.uuid)}
                      onKeyDown={e => e.key === 'Enter' && handleRenameFolder(f.uuid)}
                      className="px-2 py-1 text-xs border rounded-md w-24" autoFocus />
                  ) : (
                    <button onClick={() => setSelectedFolder(f.uuid)}
                      onDoubleClick={() => { setRenamingFolder(f.uuid); setRenameFolderName(f.name); }}
                      className={`px-2 py-1 text-xs rounded-md ${selectedFolder === f.uuid ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {f.name} ({f.strategy_count})
                    </button>
                  )}
                  <button onClick={() => handleDeleteFolder(f.uuid)}
                    className="hidden group-hover:block absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs leading-none">
                    x
                  </button>
                </div>
              ))}
              {showNewFolder ? (
                <input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                  onBlur={handleCreateFolder} onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
                  placeholder="Folder name..." className="px-2 py-1 text-xs border rounded-md w-24" autoFocus />
              ) : (
                <button onClick={() => setShowNewFolder(true)}
                  className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200">+ Folder</button>
              )}
            </div>

            {/* Search */}
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search strategies..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-3" />

            {/* Strategy list */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredStrategies.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No strategies yet</p>
              ) : filteredStrategies.map(s => (
                <div key={s.uuid}
                  onClick={() => { navigate(`/legal-strategy/${s.uuid}`); if (isMobile) setShowMobileSidebar(false); }}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors group ${
                    id === s.uuid ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{s.title || 'Processing...'}</h3>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{s.goal_text?.substring(0, 80)}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${statusColor(s.status)}`}>{s.status}</span>
                        {s.legal_domain && <span className="text-xs text-gray-400">{s.legal_domain}</span>}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{new Date(s.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="hidden group-hover:flex flex-col gap-1 ml-2">
                      {s.status === 'failed' && (
                        <button onClick={async (e) => { e.stopPropagation(); try { await fetch(`/api/legal-strategy/${s.uuid}/rerun`, { method: 'POST', credentials: 'include' }); navigate(`/legal-strategy/${s.uuid}`); loadStrategies(); } catch {} }}
                          className="text-primary-500 hover:text-primary-700 text-xs font-medium">Retry</button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(s.uuid); }}
                        className="text-gray-400 hover:text-red-500 text-xs">Del</button>
                    </div>
                  </div>
                  {/* Folder move dropdown */}
                  {folders.length > 0 && (
                    <select
                      value={s.folder_id ? folders.find(f => f.id === s.folder_id)?.uuid || '' : ''}
                      onClick={e => e.stopPropagation()}
                      onChange={e => handleMoveToFolder(s.uuid, e.target.value || null)}
                      className="mt-2 w-full text-xs border border-gray-200 rounded px-1 py-0.5 text-gray-500 hidden group-hover:block">
                      <option value="">Unfiled</option>
                      {folders.map(f => <option key={f.uuid} value={f.uuid}>{f.name}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>

            {/* New strategy button */}
            <button onClick={() => navigate('/legal-strategy')}
              className="mt-3 w-full px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
              New Strategy
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {!id ? (
            /* Input Form */
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Legal Strategy Planner</h1>
              <p className="text-gray-600 mb-6">Describe your legal objective and we'll generate a comprehensive strategic plan with jurisdictional analysis, risk assessment, and actionable steps.</p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">What is your legal goal? *</label>
                  <textarea value={goalText} onChange={e => setGoalText(e.target.value)}
                    placeholder="e.g., I want to create an LLC that can fund charitable causes without being able to be traced to who owns the LLC"
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                    required disabled={submitting} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Jurisdiction (optional)</label>
                    <input type="text" value={jurisdiction} onChange={e => setJurisdiction(e.target.value)}
                      placeholder="e.g., Delaware, United States"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={submitting} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timeline (optional)</label>
                    <input type="text" value={timeline} onChange={e => setTimeline(e.target.value)}
                      placeholder="e.g., Within 3 months"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={submitting} />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget Constraints (optional)</label>
                  <input type="text" value={budgetConstraints} onChange={e => setBudgetConstraints(e.target.value)}
                    placeholder="e.g., Under $5,000 for formation costs"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={submitting} />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Risk Tolerance</label>
                  <div className="flex gap-2">
                    {['conservative', 'moderate', 'aggressive'].map(level => (
                      <button key={level} type="button" onClick={() => setRiskTolerance(level)}
                        disabled={submitting}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          riskTolerance === level
                            ? level === 'conservative' ? 'bg-blue-100 border-blue-400 text-blue-700'
                            : level === 'moderate' ? 'bg-yellow-100 border-yellow-400 text-yellow-700'
                            : 'bg-red-100 border-red-400 text-red-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Document Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Documents (optional)</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors">
                    <input type="file" accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp" multiple
                      onChange={e => {
                        const files = Array.from(e.target.files || []);
                        setUploadedFiles(prev => [...(prev || []), ...files]);
                      }}
                      className="hidden" id="strategy-file-input" />
                    <label htmlFor="strategy-file-input" className="cursor-pointer">
                      <span className="text-gray-500 text-sm">Drop files here or <span className="text-primary-600 font-medium">browse</span></span>
                      <br /><span className="text-xs text-gray-400">PDF, Word, Excel, PowerPoint, TXT</span>
                    </label>
                  </div>
                  {uploadedFiles?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {uploadedFiles.map((f, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm">
                          <span className="truncate">📎 {f.name}</span>
                          <button type="button" onClick={() => setUploadedFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 ml-2 text-xs">Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
                )}

                <button type="submit" disabled={submitting || !goalText.trim()}
                  className="w-full px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {submitting ? 'Creating Strategy...' : 'Generate Legal Strategy'}
                </button>
              </form>
            </div>
          ) : (
            /* Strategy View */
            <div>
              {/* Progress View — only for NEW strategies (no existing phases) */}
              {(loading || strategy?.status === 'processing' || strategy?.status === 'pending') && strategy && phases.length === 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Generating Strategy...</h2>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div className="bg-primary-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${strategy.progress || 0}%` }} />
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{strategy.progress_message || 'Starting...'} ({strategy.progress || 0}%)</p>
                  {strategy.steps && Array.isArray(strategy.steps) && (
                    <div style={{ marginTop: '16px' }}>
                      {strategy.steps.map((step, idx) => {
                        const isCompleted = step.status === 'completed';
                        const isInProgress = step.status === 'in_progress';
                        const isFailed = step.status === 'failed';
                        let icon = '\u25CB', color = '#9e9e9e', fontWeight = 'normal', bg = 'transparent';
                        if (isCompleted) { icon = '\u2713'; color = '#2e7d32'; }
                        else if (isInProgress) { icon = '\u25CF'; color = '#1565c0'; fontWeight = 'bold'; bg = '#e3f2fd'; }
                        else if (isFailed) { icon = '\u2717'; color = '#c62828'; }
                        return (
                          <div key={step.name || idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderRadius: '6px', backgroundColor: bg, marginBottom: '2px', transition: 'background-color 0.3s' }}>
                            <span style={{ color, fontSize: '16px', width: '20px', textAlign: 'center', animation: isInProgress ? 'pulse 1.5s ease-in-out infinite' : 'none' }}>{icon}</span>
                            <span style={{ color: isInProgress ? '#1565c0' : isCompleted ? '#2e7d32' : isFailed ? '#c62828' : '#666', fontWeight, fontSize: '14px' }}>{step.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Revision Banner — shown when revising (has existing phases) */}
              {strategy?.status === 'processing' && phases.length > 0 && (
                <div style={{ marginBottom: 16, borderRadius: 10, border: '1px solid #c7d2fe', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 16px', background: 'linear-gradient(90deg, #eef2ff, #e0e7ff, #eef2ff)', backgroundSize: '200% 100%', animation: 'shimmer 2s ease-in-out infinite', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'flex', gap: 4 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.5s infinite' }} />
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.5s infinite', animationDelay: '0.3s' }} />
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.5s infinite', animationDelay: '0.6s' }} />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#4338ca', flex: 1 }}>Updating strategy... ({strategy.progress || 0}%)</span>
                  </div>
                  <div style={{ height: 4, background: '#e0e7ff' }}>
                    <div style={{ height: '100%', background: '#6366f1', borderRadius: '0 2px 2px 0', transition: 'width 0.5s ease', width: `${strategy.progress || 0}%` }} />
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <p className="text-red-700">{error}</p>
                  <button onClick={handleRerun} className="mt-2 text-sm text-red-600 underline hover:text-red-800">Try again</button>
                </div>
              )}

              {/* Results View — show during completed OR during revision (has phases) */}
              {(strategy?.status === 'completed' || (strategy?.status === 'processing' && phases.length > 0)) && (
                <div>
                  {/* Header */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">{strategy.title}</h1>
                        <div className="flex items-center gap-3 mt-2">
                          {strategy.legal_domain && (
                            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">{strategy.legal_domain}</span>
                          )}
                          {strategy.jurisdiction && (
                            <span className="text-sm text-gray-500">{strategy.jurisdiction}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => {
                          if (cachedExplainJob && cachedExplainJob.status !== 'none') {
                            // Open modal with existing job
                            setExplainVideo(cachedExplainJob);
                          } else {
                            // Start new job
                            (async () => {
                              setExplaining(true);
                              try {
                                const r = await fetch(`/api/legal-strategy/${strategy.uuid}/explain`, { method: 'POST', credentials: 'include' });
                                const d = await r.json();
                                if (d.success) { setCachedExplainJob(d); setExplainVideo(d); }
                              } catch (e) { console.error(e); }
                              setExplaining(false);
                            })();
                          }
                        }} disabled={explaining}
                          className="px-3 py-1.5 text-sm border border-purple-300 rounded-lg hover:bg-purple-50 text-purple-700 whitespace-nowrap inline-flex items-center gap-1 disabled:opacity-50">
                          {explaining ? '⏳ Generating...' : (cachedExplainJob?.videoUrl || cachedExplainJob?.status === 'ready') ? '🎬 Watch' : cachedExplainJob?.status === 'processing' ? '🎬 In Progress' : '🎬 Explain'}
                        </button>
                        <a href={`/api/legal-strategy/${strategy.uuid}/pdf`} download
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 whitespace-nowrap inline-flex items-center gap-1">
                          📥 PDF
                        </a>
                        <button onClick={handleRerun}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 whitespace-nowrap">
                          Re-run
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Section Navigation */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {/* Pathway selector */}
                      {pathways.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8, overflowX: 'auto', paddingBottom: 4 }}>
                          <button onClick={() => setActiveTab('overview')}
                            style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === 'overview' ? 600 : 400, border: `1.5px solid ${activeTab === 'overview' ? '#6366f1' : '#e2e8f0'}`, background: activeTab === 'overview' ? '#eef2ff' : 'white', color: activeTab === 'overview' ? '#4338ca' : '#64748b', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            📋 Overview
                          </button>
                          {pathways.map((p, i) => {
                            const key = `pathway-${i}`;
                            const isActive = activeTab === key;
                            const isRec = i === (strategy.recommended_strategy_index || 0);
                            return (
                              <button key={key} onClick={() => setActiveTab(key)}
                                style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: isActive ? 600 : 400, border: `1.5px solid ${isActive ? '#059669' : isRec ? '#a7f3d0' : '#e2e8f0'}`, background: isActive ? '#ecfdf5' : isRec ? '#f0fdf4' : 'white', color: isActive ? '#065f46' : '#64748b', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, position: 'relative' }}>
                                🛤️ {p.name?.split(' ').slice(0, 4).join(' ') || `Pathway ${i + 1}`}
                                {isRec && !isActive && <span style={{ position: 'absolute', top: -4, right: -4, width: 8, height: 8, borderRadius: '50%', background: '#059669' }} />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* Analysis tabs — subordinate row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, flexShrink: 0 }}>Analysis:</span>
                        {[
                          { key: 'research', label: 'Research', icon: '⚖️' },
                          { key: 'timelines', label: 'Timelines', icon: '📅' },
                          { key: 'simulations', label: 'Simulations', icon: '🧪' },
                          { key: 'visualizations', label: 'Diagrams', icon: '📊' },
                        ].map(tab => (
                          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            style={{
                              padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: activeTab === tab.key ? 600 : 400,
                              border: `1px solid ${activeTab === tab.key ? '#6366f1' : '#e5e7eb'}`,
                              background: activeTab === tab.key ? '#eef2ff' : '#fafbfc',
                              color: activeTab === tab.key ? '#4338ca' : '#9ca3af',
                              cursor: 'pointer',
                            }}>
                            {tab.icon} {tab.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">

                    {/* Executive Summary Tab */}
                    {/* Overview Tab — Pathway Comparison Dashboard */}
                    {activeTab === 'overview' && (
                      <div>
                        {/* Pathway Comparison Cards */}
                        {pathways.length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Legal Pathways</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(pathways.length, 3)}, 1fr)`, gap: 12 }}>
                              {pathways.map((p, i) => {
                                const isRecommended = i === (strategy.recommended_strategy_index || 0);
                                const scores = p.scores || {};
                                const dims = ['protection', 'feasibility', 'simplicity', 'speed'];
                                const invertDims = ['risk', 'cost'];
                                const overallScore = p.scores ? (
                                  ((10 - (scores.risk || 5)) * 0.2 + (scores.protection || 5) * 0.25 + (scores.feasibility || 5) * 0.2 + (scores.simplicity || 5) * 0.15 + (scores.speed || 5) * 0.1 + (10 - (scores.cost || 5)) * 0.1)
                                ).toFixed(1) : null;
                                return (
                                  <div key={i} onClick={() => setActiveTab(`pathway-${i}`)}
                                    style={{ padding: 16, borderRadius: 10, border: isRecommended ? '2px solid #059669' : '1px solid #e2e8f0', background: isRecommended ? '#ecfdf5' : 'white', cursor: 'pointer', transition: 'all 0.15s' }}>
                                    {isRecommended && <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 10, background: '#059669', color: 'white', fontWeight: 700, marginBottom: 8, display: 'inline-block' }}>RECOMMENDED</span>}
                                    <h4 style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{p.name}</h4>
                                    <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, marginBottom: 10 }}>{(p.summary || '').substring(0, 120)}{p.summary?.length > 120 ? '...' : ''}</p>

                                    {p.scores && (
                                      <div style={{ marginBottom: 8 }}>
                                        {[...dims, ...invertDims].map(dim => {
                                          const val = scores[dim] || 5;
                                          const displayVal = invertDims.includes(dim) ? 10 - val : val;
                                          const label = dim.charAt(0).toUpperCase() + dim.slice(1);
                                          return (
                                            <div key={dim} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                              <span style={{ fontSize: 10, color: '#9ca3af', width: 65, flexShrink: 0 }}>{label}</span>
                                              <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${displayVal * 10}%`, background: displayVal >= 7 ? '#059669' : displayVal >= 4 ? '#d97706' : '#dc2626', borderRadius: 3, transition: 'width 0.3s' }} />
                                              </div>
                                              <span style={{ fontSize: 10, fontWeight: 600, color: '#475569', width: 16 }}>{val}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {overallScore && <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, color: overallScore >= 7 ? '#059669' : overallScore >= 5 ? '#d97706' : '#dc2626' }}>{overallScore}</div>}
                                    <div style={{ textAlign: 'center', fontSize: 11, color: '#6366f1', fontWeight: 500, marginTop: 4 }}>View details →</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Executive Summary */}
                        {strategy.executive_summary && (
                          <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                              <h3 className="text-lg font-semibold text-gray-900">Executive Summary</h3>
                              <AskStrategyQuestion sectionKey="executive-summary" contextText={strategy.executive_summary} sectionType="Executive Summary" strategyUuid={strategy.uuid} questions={questions} onNewQuestion={handleNewQuestion} />
                            </div>
                            <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-line">{strategy.executive_summary}</div>
                          </div>
                        )}

                        {/* Next Steps */}
                        {synthesis?.next_steps && synthesis.next_steps.length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Next Steps</h3>
                            <div className="space-y-2">
                              {synthesis.next_steps.map((step, i) => (
                                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                  <input type="checkbox" className="mt-1 rounded" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{step.step}</p>
                                    <div className="flex gap-2 mt-1">
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        step.priority === 'high' ? 'bg-red-100 text-red-700' :
                                        step.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-green-100 text-green-700'
                                      }`}>{step.priority}</span>
                                      {step.timeline && <span className="text-xs text-gray-500">{step.timeline}</span>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* General FAQ */}
                        {(faqs.general || []).length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Frequently Asked Questions</h3>
                            <div className="space-y-2">
                              {faqs.general.map((faq, fi) => (
                                <div key={fi} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="text-sm font-semibold text-gray-900 mb-1">Q: {faq.question}</div>
                                  <div className="text-sm text-gray-600">{faq.answer}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Disclaimers */}
                        {synthesis?.disclaimers && synthesis.disclaimers.length > 0 && (
                          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <h4 className="text-sm font-medium text-gray-500 mb-2">Disclaimers</h4>
                            <ul className="space-y-1">
                              {synthesis.disclaimers.map((d, i) => (
                                <li key={i} className="text-xs text-gray-500">{d}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Strategic Approaches Tab */}
                    {/* Per-Pathway Tabs */}
                    {activeTab.startsWith('pathway-') && (() => {
                      const pIdx = parseInt(activeTab.split('-')[1]);
                      const pw = pathways[pIdx];
                      if (!pw) return null;
                      const simResp = simulationResponses[pIdx];
                      const riskAss = riskAssessments[pIdx];
                      return (
                        <div>
                          {/* Pathway Header */}
                          <div style={{ marginBottom: 16, padding: 16, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{pw.name}</h3>
                            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{pw.summary}</p>
                            {pw.advantages?.length > 0 && (
                              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {pw.advantages.map((a, i) => <span key={i} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#ecfdf5', color: '#065f46' }}>✓ {a}</span>)}
                              </div>
                            )}
                            {pw.disadvantages?.length > 0 && (
                              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {pw.disadvantages.map((d, i) => <span key={i} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#fef2f2', color: '#991b1b' }}>✗ {d}</span>)}
                              </div>
                            )}
                          </div>

                          {/* Steps */}
                          {pw.steps?.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Implementation Steps</h4>
                              {pw.steps.map((step, si) => (
                                <div key={si} style={{ display: 'flex', gap: 10, marginBottom: 8, padding: 12, borderRadius: 8, background: '#f8fafc', borderLeft: '3px solid #6366f1' }}>
                                  <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#6366f1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{step.step_number || si + 1}</span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{step.action}</div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{step.details}</div>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                      {step.estimated_cost && <span style={{ fontSize: 11, color: '#059669' }}>{fmt(step.estimated_cost)}</span>}
                                      {step.estimated_duration && <span style={{ fontSize: 11, color: '#6366f1' }}>{step.estimated_duration}</span>}
                                      {step.form_url && <a href={step.form_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#1565c0' }}>Filing portal ↗</a>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Cost & Timeline */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                            {pw.total_estimated_cost && <div style={{ padding: 14, borderRadius: 8, background: '#ecfdf5', border: '1px solid #a7f3d0', textAlign: 'center' }}><div style={{ fontSize: 11, color: '#065f46' }}>Total Cost</div><div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>{fmt(pw.total_estimated_cost)}</div></div>}
                            {pw.timeline_summary && <div style={{ padding: 14, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}><div style={{ fontSize: 11, color: '#1e40af' }}>Timeline</div><div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>{pw.timeline_summary}</div></div>}
                          </div>

                          {/* Action buttons: LLC Formation + Tax Strategy */}
                          <div style={{ marginBottom: 16, padding: 16, borderRadius: 10, background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)', border: '1px solid #c7d2fe' }}>
                            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#4338ca', margin: '0 0 4px' }}>Take Action</h4>
                            <p style={{ fontSize: 12, color: '#6366f1', margin: '0 0 12px' }}>Start services with your strategy details pre-filled</p>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button onClick={() => {
                                if (linkedServices.llc?.uuid) {
                                  navigate('/legal-services/' + linkedServices.llc.uuid + (linkedServices.llc.status === 'completed' ? '' : ''));
                                } else {
                                  const strat = strategy;
                                  const params = new URLSearchParams();
                                  params.set('companyName', strat.title?.replace(/^.*?for\s+/i, '').replace(/\s+legal.*$/i, '') || '');
                                  params.set('state', strat.state || 'Indiana');
                                  params.set('managementType', pw.name?.toLowerCase().includes('manager') ? 'manager-managed' : 'member-managed');
                                  params.set('businessPurpose', strat.description?.substring(0, 200) || pw.summary?.substring(0, 200) || '');
                                  params.set('strategyId', strat.uuid);
                                  navigate('/legal-services?prefill=' + encodeURIComponent(params.toString()));
                                }
                              }} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#6366f1', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {linkedServices.llc?.status === 'completed' ? '🏢 View LLC Formation Results' : linkedServices.llc ? '🏢 Continue LLC Formation' : '🏢 Start LLC Formation'}
                              </button>
                              <button onClick={() => {
                                if (linkedServices.tax?.uuid) {
                                  navigate('/tax-strategy/' + linkedServices.tax.uuid);
                                } else {
                                  const strat = strategy;
                                  const params = new URLSearchParams();
                                  params.set('entityType', pw.name?.toLowerCase().includes('llc') ? 'LLC' : pw.name?.toLowerCase().includes('corp') ? 'Corporation' : 'LLC');
                                  params.set('state', strat.state || 'Indiana');
                                  params.set('goals', `Tax optimization for: ${pw.name}. ${pw.summary || ''}`);
                                  params.set('situationDescription', `Legal strategy: ${strat.title}. Pathway: ${pw.name}. ${pw.steps?.map(s => s.action).join('. ') || ''}`);
                                  params.set('legalStrategyId', strat.uuid);
                                  navigate('/tax-strategy?prefill=' + encodeURIComponent(params.toString()));
                                }
                              }} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#059669', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {linkedServices.tax?.status === 'completed' ? '📊 View Tax Strategy Results' : linkedServices.tax ? '📊 Continue Tax Strategy' : '📊 Start Tax Strategy'}
                              </button>
                            </div>
                          </div>

                          {/* Actor Responses for this pathway */}
                          {simResp && (
                            <div style={{ marginBottom: 16 }}>
                              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Actor Responses</h4>
                              {Object.entries(simResp).filter(([k]) => k !== 'strategy_index').map(([actor, resp]) => (
                                <div key={actor} style={{ padding: 10, borderRadius: 8, background: '#f8fafc', marginBottom: 6, borderLeft: '3px solid #d97706' }}>
                                  <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'capitalize' }}>{actor.replace(/_/g, ' ')}</div>
                                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{typeof resp === 'string' ? resp : JSON.stringify(resp).substring(0, 200)}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Risks for this pathway */}
                          {riskAss?.risks?.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Risks</h4>
                              {riskAss.risks.map((r, ri) => (
                                <div key={ri} style={{ padding: 10, borderRadius: 8, background: '#f8fafc', marginBottom: 6, borderLeft: `3px solid ${(r.likelihood || 5) >= 7 ? '#dc2626' : (r.likelihood || 5) >= 4 ? '#d97706' : '#059669'}` }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{r.risk || r.name}</span>
                                    <span style={{ fontSize: 11, color: '#64748b' }}>L:{r.likelihood} I:{r.impact}</span>
                                  </div>
                                  {r.mitigation && <div style={{ fontSize: 12, color: '#059669', marginTop: 2 }}>Mitigation: {r.mitigation}</div>}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Sources */}
                          {pw.sources_referenced?.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Legal Sources</h4>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {pw.sources_referenced.map((s, si) => (
                                  <button key={si} onClick={() => setActiveTab('research')}
                                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', cursor: 'pointer' }}>
                                    ⚖️ {s}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Actions Section */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <h4 style={{ fontSize: 15, fontWeight: 600 }}>Actions</h4>
                              {!pathwayActions[pIdx] && !generatingActions && (
                                <button onClick={async () => {
                                  setGeneratingActions(true);
                                  try {
                                    // Try loading saved first
                                    let r = await fetch(`/api/legal-strategy/${strategy.uuid}/actions/${pIdx}`, { credentials: 'include' });
                                    let d = await r.json();
                                    if (d.success && d.actions) { setPathwayActions(prev => ({ ...prev, [pIdx]: d.actions })); setUserData(prev => ({ ...prev, [pIdx]: d.userData || {} })); }
                                    else {
                                      r = await fetch(`/api/legal-strategy/${strategy.uuid}/actions/${pIdx}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
                                      d = await r.json();
                                      if (d.success) setPathwayActions(prev => ({ ...prev, [pIdx]: d.actions }));
                                    }
                                  } catch (e) { console.error(e); }
                                  setGeneratingActions(false);
                                }} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#6366f1', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                  Generate Actions
                                </button>
                              )}
                              {generatingActions && <span style={{ fontSize: 12, color: '#6366f1' }}>Generating actions...</span>}
                            </div>

                            {pathwayActions[pIdx]?.actions?.map((action, ai) => (
                              <div key={ai} style={{ marginBottom: 12, borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '10px 14px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                                  <div>
                                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600, textTransform: 'uppercase', marginRight: 6,
                                      background: action.priority === 'immediate' ? '#fef2f2' : action.priority === 'short_term' ? '#fef3c7' : '#ecfdf5',
                                      color: action.priority === 'immediate' ? '#dc2626' : action.priority === 'short_term' ? '#d97706' : '#059669' }}>{action.priority?.replace('_', ' ')}</span>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{action.title}</span>
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#64748b' }}>
                                    {action.estimatedCost && <span>{fmt(action.estimatedCost)}</span>}
                                    {action.deadline && <span>{action.deadline}</span>}
                                  </div>
                                </div>
                                <div style={{ padding: 14 }}>
                                  <p style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>{action.description}</p>

                                  {/* Form fields */}
                                  {action.hasForm && action.form && (
                                    <div style={{ marginBottom: 8, padding: 10, borderRadius: 6, background: '#fafbfc', border: '1px solid #e2e8f0' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <span style={{ fontWeight: 600, fontSize: 12 }}>{action.form.name} {action.form.formNumber ? `(${action.form.formNumber})` : ''}</span>
                                        {action.form.url && <a href={action.form.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#1565c0' }}>{action.form.isOnline ? 'Open form' : 'Download form'} ↗</a>}
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {action.form.fields?.map((field, fi) => {
                                          const fieldKey = `${pIdx}-${ai}-${fi}`;
                                          const savedValue = (userData[pIdx] || {})[fieldKey];
                                          const displayValue = savedValue || field.value;
                                          const isPending = field.status === 'pending' && !savedValue;
                                          return (
                                            <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 4,
                                              background: isPending ? '#fef3c7' : '#ecfdf5', border: `1px solid ${isPending ? '#fde68a' : '#a7f3d0'}` }}>
                                              <span style={{ fontSize: 11, fontWeight: 500, color: '#475569', minWidth: 120, flexShrink: 0 }}>{field.name}:</span>
                                              {isPending ? (
                                                <input type="text" placeholder={field.hint || 'Enter value...'} defaultValue={displayValue || ''}
                                                  onBlur={async (e) => {
                                                    if (!e.target.value) return;
                                                    try {
                                                      await fetch(`/api/legal-strategy/${strategy.uuid}/actions/${pIdx}/save-field`, {
                                                        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({ fieldKey, value: e.target.value })
                                                      });
                                                      setUserData(prev => ({ ...prev, [pIdx]: { ...(prev[pIdx] || {}), [fieldKey]: e.target.value } }));
                                                    } catch {}
                                                  }}
                                                  style={{ flex: 1, padding: '3px 6px', borderRadius: 3, border: '1px solid #fde68a', fontSize: 12, background: 'white', outline: 'none' }} />
                                              ) : (
                                                <span style={{ fontSize: 12, color: '#065f46', flex: 1 }}>{displayValue}</span>
                                              )}
                                              <span style={{ fontSize: 9, fontWeight: 600, color: isPending ? '#92400e' : '#059669' }}>{isPending ? 'PENDING' : 'KNOWN'}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {/* Steps */}
                                  {action.steps?.length > 0 && !action.hasForm && (
                                    <div>
                                      {action.steps.map((step, si) => <div key={si} style={{ fontSize: 12, padding: '2px 8px', background: '#f1f5f9', borderRadius: 3, marginBottom: 2 }}>{si + 1}. {step}</div>)}
                                    </div>
                                  )}

                                  {/* Resources */}
                                  {action.resources?.length > 0 && (
                                    <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                      {action.resources.map((r, ri) => <a key={ri} href={r.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#1565c0', textDecoration: 'none' }}>{r.label} ↗</a>)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* FAQ for this pathway */}
                          {(faqs[pIdx] || []).length > 0 && (
                            <div>
                              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#6366f1' }}>Frequently Asked Questions</h4>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {faqs[pIdx].map((faq, fi) => (
                                  <div key={fi} style={{ padding: 12, borderRadius: 8, background: '#fafbfc', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', marginBottom: 4 }}>Q: {faq.question}</div>
                                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{faq.answer}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Old approaches tab — backward compatible */}
                    {activeTab === 'approaches' && formulation && (
                      <div className="space-y-4">
                        {(formulation.strategies || []).map((strat, idx) => {
                          const isRecommended = idx === strategy.recommended_strategy_index;
                          return (
                            <details key={idx} open={isRecommended} className={`border rounded-xl overflow-hidden ${isRecommended ? 'border-green-400' : 'border-gray-200'}`}>
                              <summary className={`p-4 cursor-pointer ${isRecommended ? 'bg-green-50' : 'bg-gray-50'} hover:bg-gray-100`}>
                                <div className="inline-flex items-center gap-2">
                                  {isRecommended && <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded">RECOMMENDED</span>}
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    strat.approach_type === 'conservative' ? 'bg-blue-100 text-blue-700' :
                                    strat.approach_type === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>{strat.approach_type}</span>
                                  <span className="font-semibold text-gray-900">{strat.name}</span>
                                  <AskStrategyQuestion sectionKey={`approach-${idx}`} contextText={strat.summary} sectionType={`Strategy: ${strat.name}`} strategyUuid={strategy.uuid} questions={questions} onNewQuestion={handleNewQuestion} />
                                </div>
                              </summary>
                              <div className="p-4 border-t border-gray-200">
                                <p className="text-sm text-gray-700 mb-4">{strat.summary}</p>

                                {/* Steps */}
                                {strat.steps && strat.steps.length > 0 && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2">Step-by-Step Plan</h5>
                                    <div className="space-y-2">
                                      {strat.steps.map((step, si) => (
                                        <div key={si} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                                          <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">{step.step_number || si + 1}</span>
                                          <div className="flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                              <p className="text-sm font-medium text-gray-900">{step.action}</p>
                                              <AskStrategyQuestion sectionKey={`approach-${idx}-step-${si}`} contextText={`${step.action}: ${step.details || ''}`} sectionType={`Step ${si + 1}: ${step.action}`} strategyUuid={strategy.uuid} questions={questions} onNewQuestion={handleNewQuestion} />
                                            </div>
                                            <p className="text-xs text-gray-600 mt-1">{step.details}</p>
                                            {step.legal_basis && <p className="text-xs text-indigo-600 mt-1">Legal basis: {step.legal_basis}</p>}
                                            <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                                              {step.estimated_duration && <span>Duration: {step.estimated_duration}</span>}
                                              {step.estimated_cost && <span>Cost: {step.estimated_cost}</span>}
                                            </div>
                                            {step.url && <a href={step.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-xs text-primary-600 hover:underline">Form / Resource →</a>}
                                            {step.urls && step.urls.map((u, ui) => (
                                              <a key={ui} href={u.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 mr-3 text-xs text-primary-600 hover:underline">{u.label || 'Resource'} →</a>
                                            ))}
                                            {step.form_url && <a href={step.form_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-xs text-primary-600 hover:underline">Official Form →</a>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Details grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  {strat.required_entities?.length > 0 && (
                                    <div><h5 className="font-medium text-gray-700 mb-1">Required Entities</h5>
                                      <ul className="list-disc list-inside text-gray-600 text-xs">{strat.required_entities.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                    </div>
                                  )}
                                  {strat.required_filings?.length > 0 && (
                                    <div><h5 className="font-medium text-gray-700 mb-1">Required Filings</h5>
                                      <ul className="list-disc list-inside text-gray-600 text-xs">{strat.required_filings.map((f, i) => <li key={i}>{f}</li>)}</ul>
                                    </div>
                                  )}
                                  {strat.required_documents?.length > 0 && (
                                    <div><h5 className="font-medium text-gray-700 mb-1">Required Documents</h5>
                                      <ul className="list-disc list-inside text-gray-600 text-xs">{strat.required_documents.map((d, i) => <li key={i}>{d}</li>)}</ul>
                                    </div>
                                  )}
                                  {strat.compliance_requirements?.length > 0 && (
                                    <div><h5 className="font-medium text-gray-700 mb-1">Compliance Requirements</h5>
                                      <ul className="list-disc list-inside text-gray-600 text-xs">{strat.compliance_requirements.map((c, i) => <li key={i}>{c}</li>)}</ul>
                                    </div>
                                  )}
                                </div>

                                <div className="flex gap-4 mt-4 text-sm text-gray-600">
                                  {strat.total_estimated_cost && <span><strong>Est. Cost:</strong> {strat.total_estimated_cost}</span>}
                                  {strat.timeline_summary && <span><strong>Timeline:</strong> {strat.timeline_summary}</span>}
                                  {strat.recommended_jurisdiction && <span><strong>Jurisdiction:</strong> {strat.recommended_jurisdiction}</span>}
                                </div>
                              </div>
                            </details>
                          );
                        })}
                      </div>
                    )}

                    {/* Jurisdictional Analysis Tab */}
                    {(activeTab === 'research' || activeTab === 'jurisdictional') && (
                      <div>
                        {!research && <p className="text-gray-500 text-sm p-4">Jurisdictional research will be available after the analysis completes.</p>}

                        {/* Jurisdictional Recommendation */}
                        {research?.jurisdictional_recommendation && (
                          <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                            <h3 className="text-lg font-semibold text-indigo-900 mb-2">Jurisdictional Recommendation</h3>
                            <p className="text-sm text-indigo-800">{research.jurisdictional_recommendation}</p>
                          </div>
                        )}

                        {/* Federal Law */}
                        {research?.federal_law && (
                          <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                              <h3 className="text-lg font-semibold text-gray-900">Federal Law</h3>
                              <AskStrategyQuestion sectionKey="federal-law" contextText={JSON.stringify(research?.federal_law).substring(0, 500)} sectionType="Federal Law" strategyUuid={strategy.uuid} questions={questions} onNewQuestion={handleNewQuestion} />
                            </div>
                            {research?.federal_law.applicable_statutes?.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Applicable Statutes</h4>
                                <div className="space-y-2">
                                  {research?.federal_law.applicable_statutes.map((s, i) => (
                                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                                      <div className="flex items-start justify-between">
                                        <div>
                                          {s.url ? (
                                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:underline">{s.name} ↗</a>
                                          ) : (
                                            <p className="text-sm font-medium text-gray-900">{s.name}</p>
                                          )}
                                          <p className="text-xs text-indigo-600">{s.citation}</p>
                                        </div>
                                      </div>
                                      <p className="text-xs text-gray-600 mt-1">{s.relevance}</p>
                                      {s.key_provisions && <p className="text-xs text-gray-500 mt-1">{s.key_provisions}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {research?.federal_law.applicable_regulations?.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Applicable Regulations</h4>
                                <div className="space-y-2">
                                  {research?.federal_law.applicable_regulations.map((r, i) => (
                                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                                      <p className="text-sm font-medium text-gray-900">{r.name}</p>
                                      <p className="text-xs text-indigo-600">{r.cfr_citation} {r.agency && `(${r.agency})`}</p>
                                      <p className="text-xs text-gray-600 mt-1">{r.relevance}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* State Comparison */}
                        {research?.state_law_comparison?.length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">State Law Comparison</h3>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-50">
                                    <th className="text-left px-3 py-2 font-medium text-gray-700">State</th>
                                    <th className="text-left px-3 py-2 font-medium text-gray-700">Favorability</th>
                                    <th className="text-left px-3 py-2 font-medium text-gray-700">Advantages</th>
                                    <th className="text-left px-3 py-2 font-medium text-gray-700">Disadvantages</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {research?.state_law_comparison.map((state, i) => (
                                    <tr key={i} className="border-t border-gray-200">
                                      <td className="px-3 py-3 font-medium text-gray-900">{state.state}</td>
                                      <td className="px-3 py-3">
                                        <div className="flex items-center gap-2">
                                          <div className="w-16 bg-gray-200 rounded-full h-2">
                                            <div className={`h-2 rounded-full ${state.favorability_score >= 7 ? 'bg-green-500' : state.favorability_score >= 4 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                              style={{ width: `${(state.favorability_score / 10) * 100}%` }} />
                                          </div>
                                          <span className="text-xs text-gray-600">{state.favorability_score}/10</span>
                                        </div>
                                      </td>
                                      <td className="px-3 py-3">
                                        <ul className="text-xs text-gray-600 space-y-1">{(state.advantages || []).map((a, j) => <li key={j}>+ {a}</li>)}</ul>
                                      </td>
                                      <td className="px-3 py-3">
                                        <ul className="text-xs text-gray-600 space-y-1">{(state.disadvantages || []).map((d, j) => <li key={j}>- {d}</li>)}</ul>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Jurisdictional Recommendation */}
                        {research.jurisdictional_recommendation && (
                          <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                            <h4 className="text-sm font-semibold text-indigo-900 mb-1">Jurisdictional Recommendation</h4>
                            <p className="text-sm text-indigo-800">{research.jurisdictional_recommendation}</p>
                          </div>
                        )}

                        {/* International Law */}
                        {research.international_law && (research.international_law.applicable_treaties?.length > 0 || research.international_law.foreign_jurisdictions?.length > 0) && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">International Law</h3>
                            {research.international_law.applicable_treaties?.map((t, i) => (
                              <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2">
                                <p className="text-sm font-medium text-gray-900">{t.name}</p>
                                <p className="text-xs text-gray-600">{t.relevance}</p>
                              </div>
                            ))}
                            {research.international_law.foreign_jurisdictions?.map((j, i) => (
                              <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2">
                                <p className="text-sm font-medium text-gray-900">{j.country}</p>
                                <p className="text-xs text-gray-600">{j.relevant_laws}</p>
                                <p className="text-xs text-gray-500">{j.considerations}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Legal Mechanisms */}
                        {research.legal_mechanisms?.length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Legal Mechanisms</h3>
                            <div className="space-y-2">
                              {research.legal_mechanisms.map((m, i) => (
                                <details key={i} className="border border-gray-200 rounded-lg">
                                  <summary className="p-3 cursor-pointer bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-900">{m.mechanism}</summary>
                                  <div className="p-3 text-xs text-gray-600 border-t border-gray-200">
                                    <p>{m.description}</p>
                                    {m.advantages && <p className="mt-1 text-green-700">Advantages: {m.advantages}</p>}
                                    {m.limitations && <p className="mt-1 text-red-700">Limitations: {m.limitations}</p>}
                                  </div>
                                </details>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Precedents */}
                        {research?.precedents?.length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Precedents & Examples</h3>
                            <div className="space-y-2">
                              {research?.precedents.map((p, i) => (
                                <div key={i} className="p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{p.case_or_example}</p>
                                      {p.citation && <p className="text-xs text-indigo-600">{p.citation}</p>}
                                    </div>
                                    {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline">Source</a>}
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">{p.relevance}</p>
                                  {p.outcome && <p className="text-xs text-gray-500 mt-1">Outcome: {p.outcome}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actor Simulation Tab */}
                    {activeTab === 'simulation' && simulation && (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">Actor Response Simulation</h3>
                          <AskStrategyQuestion sectionKey="simulation" contextText={simulation.interaction_dynamics || ''} sectionType="Actor Simulation" strategyUuid={strategy.uuid} questions={questions} onNewQuestion={handleNewQuestion} />
                        </div>

                        {(simulation.actors || []).map((actor, ai) => (
                          <details key={ai} className="border border-gray-200 rounded-xl mb-3 overflow-hidden">
                            <summary className="p-4 cursor-pointer bg-gray-50 hover:bg-gray-100">
                              <div className="inline-flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{actor.actor_name}</span>
                                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">{actor.actor_type}</span>
                              </div>
                              {actor.mandate && <p className="text-xs text-gray-500 mt-1 ml-4">{actor.mandate}</p>}
                            </summary>
                            <div className="p-4 border-t border-gray-200">
                              {(actor.responses_by_strategy || []).map((resp, ri) => {
                                const stratName = formulation?.strategies?.[resp.strategy_index]?.name || `Strategy ${resp.strategy_index}`;
                                return (
                                  <div key={ri} className="mb-4 last:mb-0">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-sm font-medium text-gray-700">{stratName}</span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        resp.overall_risk_level === 'low' ? 'bg-green-100 text-green-700' :
                                        resp.overall_risk_level === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                                        resp.overall_risk_level === 'high' ? 'bg-orange-100 text-orange-700' :
                                        'bg-red-100 text-red-700'
                                      }`}>{resp.overall_risk_level} risk</span>
                                    </div>
                                    <div className="space-y-1 ml-4">
                                      {(resp.likely_actions || []).map((action, lai) => (
                                        <div key={lai} className="p-2 bg-gray-50 rounded text-xs">
                                          <div className="flex items-center gap-2">
                                            <span className={`px-1.5 py-0.5 rounded font-medium ${
                                              action.probability === 'high' ? 'bg-red-100 text-red-700' :
                                              action.probability === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                              'bg-green-100 text-green-700'
                                            }`}>{action.probability_pct || action.probability}%</span>
                                            <span className="text-gray-900 font-medium">{action.action}</span>
                                          </div>
                                          <p className="text-gray-500 mt-1">{action.reasoning}</p>
                                          {action.timeline && <p className="text-gray-400 mt-0.5">Timeline: {action.timeline}</p>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        ))}

                        {simulation.interaction_dynamics && (
                          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <h4 className="text-sm font-semibold text-amber-900 mb-1">Interaction Dynamics</h4>
                            <p className="text-sm text-amber-800">{simulation.interaction_dynamics}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Risk Analysis Tab */}
                    {activeTab === 'risk' && riskAnalysis && (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">Risk Analysis</h3>
                          <AskStrategyQuestion sectionKey="risk-analysis" contextText={riskAnalysis.comparative_risk_summary || ''} sectionType="Risk Analysis" strategyUuid={strategy.uuid} questions={questions} onNewQuestion={handleNewQuestion} />
                        </div>

                        {(riskAnalysis.risk_assessments || []).map((assessment, ai) => (
                          <div key={ai} className="mb-6 border border-gray-200 rounded-xl overflow-hidden">
                            <div className="p-4 bg-gray-50 flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-gray-900">{assessment.strategy_name}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                    assessment.overall_risk_level === 'low' ? 'bg-green-100 text-green-700' :
                                    assessment.overall_risk_level === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                                    assessment.overall_risk_level === 'high' ? 'bg-orange-100 text-orange-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>{assessment.overall_risk_level} risk</span>
                                  <span className="text-sm text-gray-500">Score: {assessment.overall_risk_score}/10</span>
                                </div>
                              </div>
                            </div>
                            <div className="p-4">
                              <div className="space-y-2">
                                {(assessment.risks || []).map((risk, ri) => (
                                  <details key={ri} className={`border rounded-lg ${risk.is_deal_breaker ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                                    <summary className="p-3 cursor-pointer hover:bg-gray-50">
                                      <div className="inline-flex items-center gap-2">
                                        {risk.is_deal_breaker && <span className="text-xs px-1.5 py-0.5 bg-red-500 text-white rounded font-bold">DEAL BREAKER</span>}
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                          risk.severity_score >= 60 ? 'bg-red-100 text-red-700' :
                                          risk.severity_score >= 30 ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-green-100 text-green-700'
                                        }`}>Severity: {risk.severity_score}</span>
                                        <span className="text-sm text-gray-900">{risk.risk}</span>
                                      </div>
                                    </summary>
                                    <div className="p-3 border-t border-gray-200 text-xs">
                                      <div className="grid grid-cols-2 gap-2 mb-2">
                                        <div><span className="text-gray-500">Category:</span> <span className="text-gray-700">{risk.category}</span></div>
                                        <div><span className="text-gray-500">Triggered by:</span> <span className="text-gray-700">{risk.triggered_by}</span></div>
                                        <div><span className="text-gray-500">Likelihood:</span> <span className="text-gray-700">{risk.likelihood} ({risk.likelihood_score}/10)</span></div>
                                        <div><span className="text-gray-500">Impact:</span> <span className="text-gray-700">{risk.impact} ({risk.impact_score}/10)</span></div>
                                      </div>
                                      <div className="mt-2 p-2 bg-blue-50 rounded"><strong className="text-blue-700">Mitigation:</strong> <span className="text-blue-800">{risk.mitigation}</span></div>
                                      <div className="mt-1 p-2 bg-amber-50 rounded"><strong className="text-amber-700">Contingency:</strong> <span className="text-amber-800">{risk.contingency_plan}</span></div>
                                    </div>
                                  </details>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}

                        {riskAnalysis.comparative_risk_summary && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Comparative Risk Summary</h4>
                            <p className="text-sm text-gray-600">{riskAnalysis.comparative_risk_summary}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Scenario Timelines Tab */}
                    {activeTab === 'timelines' && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Multi-Year Scenario Timelines</h3>
                        <p className="text-sm text-gray-500 mb-4">Fictitious but realistic timelines showing how different outcomes could unfold over {timelineScenarios?.timelineYears || 'several'} years.</p>

                        {timelineScenarios?.scenarios?.map((scenario, si) => {
                          const scenarioColors = { best_case: { bg: '#ecfdf5', border: '#059669', label: '#065f46', badge: 'bg-emerald-100 text-emerald-700' }, moderate_case: { bg: '#fef3c7', border: '#d97706', label: '#92400e', badge: 'bg-amber-100 text-amber-700' }, worst_case: { bg: '#fef2f2', border: '#dc2626', label: '#991b1b', badge: 'bg-red-100 text-red-700' } };
                          const sc = scenarioColors[scenario.id] || scenarioColors.moderate_case;
                          return (
                            <div key={si} style={{ marginBottom: 24, borderRadius: 12, border: `2px solid ${sc.border}`, overflow: 'hidden' }}>
                              <div style={{ padding: '16px 20px', background: sc.bg }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <h4 style={{ fontSize: 17, fontWeight: 700, color: sc.label, margin: 0 }}>{scenario.label}</h4>
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${sc.badge}`}>{Math.round((scenario.probability || 0) * 100)}% probability</span>
                                </div>
                                {scenario.narrative && <p style={{ fontSize: 13, color: sc.label, marginTop: 6, opacity: 0.85 }}>{scenario.narrative}</p>}
                              </div>

                              <div style={{ padding: 20 }}>
                                {/* Year-by-year checkpoints */}
                                {scenario.checkpoints?.map((cp, ci) => (
                                  <div key={ci} style={{ display: 'flex', gap: 16, marginBottom: 20, position: 'relative' }}>
                                    {/* Year badge + timeline line */}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 48 }}>
                                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: cp.overallRiskLevel >= 7 ? '#dc2626' : cp.overallRiskLevel >= 4 ? '#d97706' : '#059669', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>Y{cp.year}</div>
                                      {ci < scenario.checkpoints.length - 1 && <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />}
                                    </div>
                                    {/* Content */}
                                    <div style={{ flex: 1, paddingBottom: 8 }}>
                                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{cp.milestone}</div>
                                      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: cp.overallRiskLevel >= 7 ? '#fef2f2' : cp.overallRiskLevel >= 4 ? '#fef3c7' : '#ecfdf5', color: cp.overallRiskLevel >= 7 ? '#dc2626' : cp.overallRiskLevel >= 4 ? '#d97706' : '#059669', fontWeight: 600 }}>Risk: {cp.overallRiskLevel}/10</span>
                                        {cp.financialPosition && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#f1f5f9', color: '#475569' }}>{cp.financialPosition.netPosition || cp.financialPosition.revenue}</span>}
                                      </div>

                                      {/* Risks grid */}
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                        {cp.internalRisks?.length > 0 && (
                                          <div style={{ padding: 10, borderRadius: 8, background: '#fef3c7', border: '1px solid #fde68a' }}>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Internal Risks</div>
                                            {cp.internalRisks.map((r, ri) => (
                                              <div key={ri} style={{ fontSize: 12, marginBottom: 3 }}>
                                                <span style={{ fontWeight: 500 }}>{r.risk}</span>
                                                <span style={{ fontSize: 10, color: '#92400e', marginLeft: 4 }}>L:{r.likelihood} I:{r.impact}</span>
                                                {r.mitigation && <div style={{ fontSize: 11, color: '#78716c' }}>→ {r.mitigation}</div>}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {cp.externalRisks?.length > 0 && (
                                          <div style={{ padding: 10, borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca' }}>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>External Risks</div>
                                            {cp.externalRisks.map((r, ri) => (
                                              <div key={ri} style={{ fontSize: 12, marginBottom: 3 }}>
                                                <span style={{ fontWeight: 500 }}>{r.risk}</span>
                                                <span style={{ fontSize: 10, color: '#991b1b', marginLeft: 4 }}>L:{r.likelihood} I:{r.impact}</span>
                                                {r.mitigation && <div style={{ fontSize: 11, color: '#78716c' }}>→ {r.mitigation}</div>}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {/* Critical decision */}
                                      {cp.criticalDecision && (
                                        <div style={{ padding: 8, borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12 }}>
                                          <span style={{ fontWeight: 600, color: '#1e40af' }}>Critical Decision: </span>{cp.criticalDecision}
                                        </div>
                                      )}

                                      {/* Key actions */}
                                      {cp.keyActions?.length > 0 && (
                                        <div style={{ marginTop: 6 }}>
                                          {cp.keyActions.map((a, ai) => (
                                            <span key={ai} style={{ display: 'inline-block', fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', marginRight: 4, marginBottom: 4 }}>✓ {a}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}

                                {/* Final outcome */}
                                {scenario.finalOutcome && (
                                  <div style={{ padding: 12, borderRadius: 8, background: sc.bg, border: `1px solid ${sc.border}`, marginTop: 8 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: sc.label }}>Final Outcome (Year {timelineScenarios?.timelineYears || '?'})</div>
                                    <div style={{ fontSize: 13, color: sc.label, marginTop: 4 }}>{scenario.finalOutcome}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {/* Cross-scenario insights */}
                        {timelineScenarios?.crossScenarioInsights && (
                          <div style={{ marginTop: 16, padding: 20, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Cross-Scenario Insights</h4>

                            {timelineScenarios.crossScenarioInsights.alwaysDoRegardless?.length > 0 && (
                              <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#059669', marginBottom: 6 }}>Always Do Regardless of Scenario</div>
                                {timelineScenarios.crossScenarioInsights.alwaysDoRegardless.map((a, i) => (
                                  <div key={i} style={{ fontSize: 13, padding: '4px 10px', borderRadius: 6, background: '#ecfdf5', marginBottom: 4 }}>✓ {a}</div>
                                ))}
                              </div>
                            )}

                            {timelineScenarios.crossScenarioInsights.earlyWarningSignals?.length > 0 && (
                              <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#d97706', marginBottom: 6 }}>Early Warning Signals</div>
                                {timelineScenarios.crossScenarioInsights.earlyWarningSignals.map((s, i) => (
                                  <div key={i} style={{ fontSize: 12, padding: 8, borderRadius: 6, background: '#fef3c7', marginBottom: 4, borderLeft: '3px solid #d97706' }}>
                                    <div style={{ fontWeight: 500 }}>{s.signal}</div>
                                    <div style={{ color: '#92400e', marginTop: 2 }}>Indicates: {s.indicatesScenario} → Action: {s.responseAction}</div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {timelineScenarios.crossScenarioInsights.biggestDivergencePoints?.length > 0 && (
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#6366f1', marginBottom: 6 }}>Biggest Divergence Points</div>
                                {timelineScenarios.crossScenarioInsights.biggestDivergencePoints.map((d, i) => (
                                  <div key={i} style={{ fontSize: 12, padding: 8, borderRadius: 6, background: '#eef2ff', marginBottom: 4, borderLeft: '3px solid #6366f1' }}>
                                    <div style={{ fontWeight: 500 }}>Year {d.year}: {d.event}</div>
                                    <div style={{ marginTop: 4, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                      <div style={{ padding: 4, borderRadius: 4, background: '#ecfdf5', fontSize: 11 }}>Best: {d.bestCaseResponse}</div>
                                      <div style={{ padding: 4, borderRadius: 4, background: '#fef2f2', fontSize: 11 }}>Worst: {d.worstCaseResponse}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Timeline diagrams */}
                        {diagrams?.scenario_branching && <StrategyDiagramRenderer diagramData={diagrams.scenario_branching} />}
                        {diagrams?.risk_timeline && <StrategyDiagramRenderer diagramData={diagrams.risk_timeline} />}

                        {!timelineScenarios && <p className="text-gray-500 text-sm">Timeline scenarios will appear once the strategy analysis completes.</p>}
                      </div>
                    )}

                    {/* Simulations Tab */}
                    {activeTab === 'simulations' && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">Risk Simulations</h3>
                            <p className="text-sm text-gray-500">Stress-test your legal structure against the top risks</p>
                          </div>
                          {!simulatingRisks && (
                            <button onClick={async () => {
                              setSimulatingRisks(true);
                              try {
                                const r = await fetch(`/api/legal-strategy/${strategy.uuid}/simulate-risks`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
                                const d = await r.json();
                                if (d.success) setSimulations(d.simulations);
                              } catch (e) { console.error(e); }
                              setSimulatingRisks(false);
                            }} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#6366f1', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {simulations ? '🔄 Re-simulate' : '🧪 Run Simulations'}
                            </button>
                          )}
                        </div>

                        {simulatingRisks && (
                          <div style={{ padding: 40, textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 12 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.4s infinite' }} />
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.4s infinite', animationDelay: '0.2s' }} />
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.4s infinite', animationDelay: '0.4s' }} />
                            </div>
                            <p style={{ color: '#6366f1', fontWeight: 500, fontSize: 14 }}>Running risk simulations...</p>
                            <p style={{ color: '#9ca3af', fontSize: 12 }}>Testing your legal structure against each risk scenario</p>
                          </div>
                        )}

                        {!simulations && !simulatingRisks && (
                          <div style={{ padding: 40, textAlign: 'center', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                            <span style={{ fontSize: 40 }}>🧪</span>
                            <p style={{ color: '#64748b', marginTop: 12 }}>Click "Run Simulations" to stress-test your legal structure against the top risks identified in the analysis.</p>
                          </div>
                        )}

                        {simulations && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {/* Summary */}
                            {simulations.summary && (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                                <div style={{ padding: 14, borderRadius: 8, background: '#ecfdf5', border: '1px solid #a7f3d0', textAlign: 'center' }}>
                                  <div style={{ fontSize: 24, fontWeight: 700, color: '#059669' }}>{simulations.summary.protected || 0}</div>
                                  <div style={{ fontSize: 12, color: '#065f46' }}>Protected</div>
                                </div>
                                <div style={{ padding: 14, borderRadius: 8, background: '#fef3c7', border: '1px solid #fde68a', textAlign: 'center' }}>
                                  <div style={{ fontSize: 24, fontWeight: 700, color: '#d97706' }}>{simulations.summary.vulnerable || 0}</div>
                                  <div style={{ fontSize: 12, color: '#92400e' }}>Vulnerable</div>
                                </div>
                                <div style={{ padding: 14, borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', textAlign: 'center' }}>
                                  <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>{simulations.summary.critical || 0}</div>
                                  <div style={{ fontSize: 12, color: '#991b1b' }}>Critical</div>
                                </div>
                                <div style={{ padding: 14, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                  <div style={{ fontSize: 24, fontWeight: 700, color: '#475569' }}>{simulations.summary.overallGrade || '?'}</div>
                                  <div style={{ fontSize: 12, color: '#64748b' }}>Overall Grade</div>
                                </div>
                              </div>
                            )}

                            {/* Individual simulations */}
                            {simulations.scenarios?.map((sim, i) => {
                              const gradeColors = { A: '#059669', B: '#0ea5e9', C: '#d97706', D: '#dc2626', F: '#991b1b' };
                              const gradeColor = gradeColors[sim.grade?.[0]] || '#64748b';
                              return (
                                <div key={i} style={{ borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                  <div style={{ padding: '12px 16px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                                    <div>
                                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600, textTransform: 'uppercase',
                                        background: sim.category === 'internal' ? '#dbeafe' : sim.category === 'regulatory' ? '#fef3c7' : sim.category === 'external' ? '#fce7f3' : '#f1f5f9',
                                        color: sim.category === 'internal' ? '#1e40af' : sim.category === 'regulatory' ? '#92400e' : sim.category === 'external' ? '#be185d' : '#475569',
                                        marginRight: 8 }}>{sim.category || 'risk'}</span>
                                      <span style={{ fontWeight: 600, fontSize: 14 }}>{sim.riskName}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{ fontSize: 11, color: '#9ca3af' }}>Likelihood: {sim.likelihood}/10</span>
                                      <span style={{ width: 32, height: 32, borderRadius: '50%', background: gradeColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>{sim.grade || '?'}</span>
                                    </div>
                                  </div>
                                  <div style={{ padding: 16 }}>
                                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>{sim.scenario}</p>

                                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                                      <span style={{ fontWeight: 600 }}>How your structure performs: </span>
                                      <span style={{ color: sim.outcome === 'protected' ? '#059669' : sim.outcome === 'vulnerable' ? '#d97706' : '#dc2626' }}>{sim.outcome}</span>
                                    </div>

                                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                                      <span style={{ fontWeight: 600 }}>What happens: </span>
                                      <span>{sim.impact}</span>
                                    </div>

                                    {sim.protections?.length > 0 && (
                                      <div style={{ marginBottom: 8 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', marginBottom: 4 }}>Protections in place:</div>
                                        {sim.protections.map((p, j) => <div key={j} style={{ fontSize: 12, padding: '2px 8px', background: '#ecfdf5', borderRadius: 4, marginBottom: 2 }}>✓ {p}</div>)}
                                      </div>
                                    )}

                                    {sim.vulnerabilities?.length > 0 && (
                                      <div style={{ marginBottom: 8 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', marginBottom: 4 }}>Vulnerabilities:</div>
                                        {sim.vulnerabilities.map((v, j) => <div key={j} style={{ fontSize: 12, padding: '2px 8px', background: '#fef2f2', borderRadius: 4, marginBottom: 2 }}>⚠ {v}</div>)}
                                      </div>
                                    )}

                                    {sim.recommendations?.length > 0 && (
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', marginBottom: 4 }}>Recommendations:</div>
                                        {sim.recommendations.map((r, j) => <div key={j} style={{ fontSize: 12, padding: '2px 8px', background: '#eef2ff', borderRadius: 4, marginBottom: 2 }}>→ {r}</div>)}
                                      </div>
                                    )}

                                    {sim.estimatedExposure && (
                                      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: '#dc2626' }}>
                                        Estimated exposure: {sim.estimatedExposure}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Visualizations Tab */}
                    {activeTab === 'visualizations' && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Visualizations</h3>
                        {diagrams?.decision_tree && <StrategyDiagramRenderer diagramData={diagrams.decision_tree} />}
                        {diagrams?.timeline_diagram && <StrategyDiagramRenderer diagramData={diagrams.timeline_diagram} />}
                        {diagrams?.risk_matrix_diagram && <StrategyDiagramRenderer diagramData={diagrams.risk_matrix_diagram} />}
                        {!diagrams && <p className="text-gray-500 text-sm">No visualizations available.</p>}
                      </div>
                    )}

                    {/* Conversation Tab */}
                    {activeTab === 'conversation' && (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ display: 'flex', flexDirection: 'column', height: '600px' }}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
                          <h3 className="text-lg font-semibold text-gray-900">Strategy Conversation</h3>
                          <p className="text-xs text-gray-500 mt-1">Discuss, refine, or add information. Upload documents for deeper analysis.</p>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                          {(questions || []).filter(q => q.section_key === 'global').length === 0 && !chatLoading && (
                            <p style={{ textAlign: 'center', color: '#999', fontSize: 13, marginTop: 40 }}>Start a conversation to refine your strategy. You can ask questions, provide new information, or upload documents.</p>
                          )}
                          {(questions || []).filter(q => q.section_key === 'global').map((q, i) => (
                            <div key={q.id || i} style={{ marginBottom: 16 }}>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                                <div style={{ maxWidth: '85%', padding: '10px 14px', background: q.question.startsWith('[Uploaded:') ? '#ecfdf5' : '#4f46e5', color: q.question.startsWith('[Uploaded:') ? '#065f46' : 'white', borderRadius: '14px 14px 2px 14px', fontSize: 13 }}>
                                  {q.question.startsWith('[Uploaded:') ? '📎 ' : ''}{q.question}
                                </div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <div style={{ maxWidth: '85%', padding: '10px 14px', background: '#f3f4f6', borderRadius: '14px 14px 14px 2px', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{q.answer}</div>
                              </div>
                            </div>
                          ))}
                          {chatLoading && <div style={{ display: 'flex', justifyContent: 'flex-start' }}><div style={{ padding: '10px 14px', background: '#f3f4f6', borderRadius: 14, fontSize: 13, color: '#999' }}>Thinking...</div></div>}
                          <div ref={chatEndRef} />
                        </div>

                        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, alignItems: 'center' }}>
                          <label style={{ cursor: 'pointer', padding: '8px', borderRadius: 8, border: '1px solid #d1d5db', display: 'flex', alignItems: 'center', color: uploading ? '#9ca3af' : '#6366f1' }}>
                            <input type="file" accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp" style={{ display: 'none' }} disabled={uploading}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setUploading(true);
                                try {
                                  const fd = new FormData();
                                  fd.append('file', file);
                                  const r = await fetch(`/api/legal-strategy/${strategy.uuid}/upload`, { method: 'POST', credentials: 'include', body: fd });
                                  const d = await r.json();
                                  if (d.success) handleNewQuestion(d.question);
                                } catch (err) { console.error('Upload error:', err); }
                                setUploading(false);
                                e.target.value = '';
                              }} />
                            {uploading ? '...' : '📎'}
                          </label>
                          <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)}
                            onKeyDown={async (e) => {
                              if (e.key !== 'Enter' || !chatInput.trim() || chatLoading) return;
                              setChatLoading(true);
                              try {
                                const r = await fetch(`/api/legal-strategy/${strategy.uuid}/conversation`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: chatInput.trim() }) });
                                const d = await r.json();
                                if (d.success) { handleNewQuestion(d.question); setChatInput(''); }
                              } catch {}
                              setChatLoading(false);
                              setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                            }}
                            placeholder="Ask a question, provide new info, or suggest changes..."
                            style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none' }} />
                          <button disabled={!chatInput.trim() || chatLoading}
                            onClick={async () => {
                              if (!chatInput.trim() || chatLoading) return;
                              setChatLoading(true);
                              try {
                                const r = await fetch(`/api/legal-strategy/${strategy.uuid}/conversation`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: chatInput.trim() }) });
                                const d = await r.json();
                                if (d.success) { handleNewQuestion(d.question); setChatInput(''); }
                              } catch {}
                              setChatLoading(false);
                              setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                            }}
                            style={{ padding: '10px 18px', background: (!chatInput.trim() || chatLoading) ? '#d1d5db' : '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: (!chatInput.trim() || chatLoading) ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>Send</button>
                        </div>

                        {(questions || []).filter(q => q.section_key === 'global').length > 0 && (
                          <div style={{ padding: '8px 20px', borderTop: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
                            <button onClick={async () => {
                              if (!window.confirm('Revise the strategy based on all conversation feedback?')) return;
                              try {
                                await fetch(`/api/legal-strategy/${strategy.uuid}/revise`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                                window.location.reload();
                              } catch {}
                            }} style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #6366f1', background: 'white', color: '#6366f1', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                              Revise Strategy with Conversation Feedback
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>

    {strategy?.uuid && (strategy?.status === 'completed' || (strategy?.status === 'processing' && phases.length > 0)) && (
      <StrategyAgent
        strategyUuid={strategy.uuid}
        strategyType="legal-strategy"
        onRevisionStarted={() => { startPolling(strategy.uuid); }}
        onRevisionComplete={() => { loadStrategy(strategy.uuid); loadStrategies(); }}
      />
    )}
    </div>
    </>
  );
}

export default LegalStrategy;
